import React, { useState, useMemo } from 'react';
import { Activity } from 'lucide-react';

const FormInput = ({ label, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
    <input 
      {...props}
      className="w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-3 text-base text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors" 
    />
  </div>
);

const FormSelect = ({ label, children, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
    <select
      {...props}
      className="w-full bg-slate-100 dark:bg-zinc-800 border-none rounded-lg px-4 py-3 text-base text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
    >
      {children}
    </select>
  </div>
);

export default function SteroidTaperCalculator() {
  const [startDose, setStartDose] = useState("40");
  const [tabletStrength, setTabletStrength] = useState(5);
  const [decrement, setDecrement] = useState(5);
  const [daysPerStep, setDaysPerStep] = useState(7);

  const schedule = useMemo(() => {
    const start = parseFloat(startDose);
    if (isNaN(start) || start <= 0 || decrement <= 0 || tabletStrength <= 0) return [];

    let currentDose = start;
    const taperSchedule = [];
    while (currentDose > 0) {
      const numTablets = currentDose / tabletStrength;
      taperSchedule.push({
        dosage: `${currentDose} mg`,
        instructions: `${numTablets.toFixed(1)} tab${numTablets !== 1 ? 's' : ''} daily for ${daysPerStep} days`
      });
      currentDose -= decrement;
    }
    return taperSchedule;
  }, [startDose, tabletStrength, decrement, daysPerStep]);

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2 mb-4"><Activity size={20}/> Steroid Taper Generator</h3>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <FormInput label="Starting Dose (mg)" value={startDose} onChange={(e) => setStartDose(e.target.value)} type="number" />
        <FormSelect label="Tablet Strength (mg)" value={tabletStrength} onChange={(e) => setTabletStrength(parseFloat(e.target.value))}>
          {[1, 2.5, 5, 10, 20].map(s => <option key={s} value={s}>{s} mg</option>)}
        </FormSelect>
        <FormInput label="Decrease By (mg)" value={decrement} onChange={(e) => setDecrement(parseFloat(e.target.value))} type="number" />
        <FormInput label="Days Per Step" value={daysPerStep} onChange={(e) => setDaysPerStep(parseInt(e.target.value, 10))} type="number" />
      </div>
      <div className="bg-slate-100 dark:bg-zinc-800 rounded-lg p-4">
        <h4 className="font-semibold mb-2 text-gray-700 dark:text-gray-300">Generated Schedule:</h4>
        {schedule.length > 0 ? (
          <ul className="space-y-2 text-sm">
            {schedule.map((step, i) => (
              <li key={i} className="flex justify-between">
                <span className="font-medium">{step.dosage}</span>
                <span>{step.instructions}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">Enter valid parameters to generate a schedule.</p>
        )}
      </div>
    </div>
  );
}
